import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useForm } from "react-hook-form";
import FormControl from "@material-ui/core/FormControl";
import FormGroup from "@material-ui/core/FormGroup";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { createLocation } from "../util/db";
import { DateContext } from "../providers/DateProvider";
import { toast } from "react-hot-toast";

// ---------- helpers (same style as we discussed) ----------
const pad2 = (n) => String(n).padStart(2, "0");

const startOfLocalDay = (dateLike) => {
  const d = dateLike instanceof Date ? new Date(dateLike) : new Date(dateLike);
  d.setHours(0, 0, 0, 0);
  return d;
};

const toDayKey = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const dayKeyFromMs = (ms) => toDayKey(startOfLocalDay(new Date(ms)));

// Parse "YYYY-MM-DD" into a Date at LOCAL start-of-day (no UTC parsing)
const dayKeyToLocalDate = (dayKey) => {
  const [y, m, d] = dayKey.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

// For dialog copy-day display only
const dayKeyToPretty = (dayKey) => {
  const d = dayKeyToLocalDate(dayKey);
  return d.toDateString(); // "Wed Jan 28 2026"
};

// Today's key (for display "today")
const todayKey = toDayKey(startOfLocalDay(new Date()));

function CopyLocationSelectGroupDialog(props) {
  /**
   * EXPECTATION NOW:
   * props.copyDay === dayKey string "YYYY-MM-DD"
   */
  const [pending, setPending] = useState(false);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [groupToCopyTo, setGroupToCopyTo] = useState(null);

  const { handleSubmit } = useForm();
  const { setSelectedDate } = useContext(DateContext);

  const copyDayKey = props.copyDay; // clarity

  const copyDayDateObj = useMemo(() => {
    return copyDayKey ? dayKeyToLocalDate(copyDayKey) : startOfLocalDay(new Date());
  }, [copyDayKey]);

  // If you still have some legacy code passing "Wed Jan 28 2026" in here,
  // it will NOT be safe. Make sure caller passes dayKey only.

  const handleChange = (event) => {
    setGroupToCopyTo(event.target.value);
  };

  useEffect(() => {
    if (!props.listOfGroupsAndDays?.length || !copyDayKey) {
      setFilteredGroups(props.category === "production" ? props.listOfGroupsAndDays ?? [] : []);
      return;
    }

    if (props.category === "production") {
      setFilteredGroups(props.listOfGroupsAndDays);
      return;
    }

    const dayKey = copyDayKey;

    const next = (props.listOfGroupsAndDays ?? []).filter((group) => {
      // ✅ Preferred: dayKey range overlap
      if (group.startDay && group.endDay) {
        return group.startDay <= dayKey && group.endDay >= dayKey;
      }

      // ⛑️ Legacy ms range fallback (convert to dayKey for compare)
      if (group.startDate != null && group.endDate != null) {
        const gStart = dayKeyFromMs(group.startDate);
        const gEnd = dayKeyFromMs(group.endDate);
        return gStart <= dayKey && gEnd >= dayKey;
      }

      // ⛑️ Legacy string day fallback
      // group.day was like "Wed Jan 28 2026"
      if (group.day) {
        return group.day === copyDayDateObj.toDateString();
      }

      return false;
    });

    setFilteredGroups(next);
    }, [
      copyDayKey,
      copyDayDateObj,
      props.category,
      props.listOfGroupsAndDays,
    ]);

  const onSubmit = () => {
    if (!groupToCopyTo) return;
    setPending(true);
    copyLocation();
  };

  const copyLocation = () => {
    if (!groupToCopyTo) return;

    const base = {
      name: props.location?.name?.toUpperCase(),
      type: props.location?.type,
      typeOrder: props.location?.typeOrder,
      mapData: props.location?.mapData,
      category: props.location?.category,
      groupId: groupToCopyTo,
      ...(props.category !== "production" ? { setName: props.location?.setName } : {}),
    };

    createLocation({
      productionId: props.location?.productionId,
      productionName: props.productionName,
      ...base,

      // ✅ Ensure copied location is pinned to the intended day
      ...(copyDayKey ? { dayKey: copyDayKey } : {}),

      // ⛑️ Optional migration support (if other code still expects these)
      // If you want to keep locations stamped with a legacy startDate/endDate:
      ...(copyDayKey
        ? {
            startDate: copyDayDateObj.getTime(),
            endDate: copyDayDateObj.getTime(),
          }
        : {}),
    }).then((response) => {
      if (response.success) {
        toast.success("Successfully copied location");

        // Jump admin UI to the copy day (provider will normalize anyway)
        if (copyDayKey && props.category !== "production") {
          setSelectedDate(copyDayDateObj);
        }
      } else {
        toast.error("Failed to copy location. Try again later.");
      }

      handleCloseCopyLocationSelectGroupDialog();
    });
  };

  const handleCloseCopyLocationSelectGroupDialog = () => {
    setPending(false);
    props.setOpen(false);
  };

  const prettyCopyDay = copyDayKey ? dayKeyToPretty(copyDayKey) : "";

  return (
    <Dialog open={props.open} onClose={handleCloseCopyLocationSelectGroupDialog}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle style={{ paddingBottom: 0 }}>
          {filteredGroups?.length > 0 && props.category !== "production"
            ? `Copy "${props.location?.type}${
                props.location?.setName ? " - " + props.location?.setName : ""
              } - ${props.location?.name}" to ${
                copyDayKey === todayKey ? "today" : prettyCopyDay
              }`
            : filteredGroups?.length > 0 && props.category === "production"
            ? `Copy ${props.location?.name}`
            : `Sorry, there are no groups on ${
                copyDayKey === todayKey ? "today" : prettyCopyDay
              } to copy to!`}
        </DialogTitle>

        <DialogContent>
          {!!filteredGroups?.length && (
            <DialogContentText>Pick a group to copy to:</DialogContentText>
          )}

          <Grid item>
            <FormGroup>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="groups"
                  name="groups"
                  value={groupToCopyTo}
                  onChange={handleChange}
                >
                  {filteredGroups?.map((group) => (
                    <FormControlLabel
                      key={group.id}
                      value={group.id}
                      control={<Radio />}
                      label={group.name}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </FormGroup>
          </Grid>

          <DialogActions>
            <Button
              variant="contained"
              autoFocus
              onClick={handleCloseCopyLocationSelectGroupDialog}
              color="secondary"
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="large"
              type="submit"
              disabled={pending || !filteredGroups?.length || !groupToCopyTo}
            >
              {!pending && <span>COPY</span>}
              {pending && <CircularProgress size={28} />}
            </Button>
          </DialogActions>
        </DialogContent>
      </form>
    </Dialog>
  );
}

export default CopyLocationSelectGroupDialog;